import React from 'react';

import type {
  DocumentationFeedback,
  PossibleCause,
  StepNode,
} from './TSFADAnalysis.shared';

type StylesMap = Record<string, any>;

type QuoteInactivateModalTarget = {
  quoteId: string;
  title: string;
  analysisId?: string | null;
};

type QuoteInactivateModalProps = {
  open: boolean;
  styles: StylesMap;
  quoteInactivateTarget: QuoteInactivateModalTarget | null;
  quoteInactivationReason: string;
  quoteInactivationError: string | null;
  quoteStateChangeId: string | null;
  onReasonChange: (value: string) => void;
  onClose: () => void;
  onConfirm: () => void;
};

export const TSFADQuoteInactivateModal: React.FC<QuoteInactivateModalProps> = ({
  open,
  styles,
  quoteInactivateTarget,
  quoteInactivationReason,
  quoteInactivationError,
  quoteStateChangeId,
  onReasonChange,
  onClose,
  onConfirm,
}) => {
  if (!open || !quoteInactivateTarget) return null;

  return (
    <div style={styles.modal} onClick={onClose}>
      <div
        style={{ ...styles.modalContent, maxWidth: '560px' }}
        onClick={(e) => e.stopPropagation()}
      >
        <div style={styles.modalHeader}>
          <h3 style={styles.modalTitle}>Inactivate Quote</h3>
          <div style={{ color: '#6b7280', fontSize: '0.875rem' }}>
            {quoteInactivateTarget.title}
          </div>
        </div>

        <div style={styles.modalBody}>
          <div style={styles.inlineNotice}>
            Inactivating this quote removes it from active quote workflow. Enter a reason
            for audit history.
          </div>
          <div>
            <label
              style={{ display: 'block', fontWeight: 600, marginBottom: '0.35rem' }}
            >
              Reason (required)
            </label>
            <textarea
              style={{ ...styles.textarea, minHeight: '110px' }}
              value={quoteInactivationReason}
              onChange={(e) => onReasonChange(e.target.value)}
              placeholder="Why is this quote being inactivated?"
              disabled={quoteStateChangeId === quoteInactivateTarget.quoteId}
            />
            {quoteInactivationError && (
              <div style={{ color: '#b91c1c', fontSize: '0.85rem', marginTop: '0.4rem' }}>
                {quoteInactivationError}
              </div>
            )}
          </div>
        </div>

        <div style={styles.modalFooter}>
          <button
            style={{ ...styles.button, ...styles.dangerButton }}
            onClick={onConfirm}
            disabled={quoteStateChangeId === quoteInactivateTarget.quoteId}
          >
            {quoteStateChangeId === quoteInactivateTarget.quoteId
              ? 'Updating...'
              : 'Confirm Inactivate'}
          </button>
          <button
            style={{ ...styles.button, ...styles.secondaryButton }}
            onClick={onClose}
            disabled={quoteStateChangeId === quoteInactivateTarget.quoteId}
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
};

type QuoteDeleteModalProps = {
  open: boolean;
  styles: StylesMap;
  quoteDeleteTarget: QuoteInactivateModalTarget | null;
  quoteDeleteError: string | null;
  quoteDeleteId: string | null;
  onClose: () => void;
  onConfirm: () => void;
};

export const TSFADQuoteDeleteModal: React.FC<QuoteDeleteModalProps> = ({
  open,
  styles,
  quoteDeleteTarget,
  quoteDeleteError,
  quoteDeleteId,
  onClose,
  onConfirm,
}) => {
  if (!open || !quoteDeleteTarget) return null;

  return (
    <div style={styles.modal} onClick={onClose}>
      <div
        style={{ ...styles.modalContent, maxWidth: '560px' }}
        onClick={(e) => e.stopPropagation()}
      >
        <div style={styles.modalHeader}>
          <h3 style={styles.modalTitle}>Delete Draft Quote</h3>
          <div style={{ color: '#6b7280', fontSize: '0.875rem' }}>
            {quoteDeleteTarget.title}
          </div>
        </div>

        <div style={styles.modalBody}>
          <div style={styles.inlineNotice}>
            Delete this draft quote permanently? This action cannot be undone.
          </div>
          {quoteDeleteError && (
            <div style={{ color: '#b91c1c', fontSize: '0.85rem', marginTop: '0.5rem' }}>
              {quoteDeleteError}
            </div>
          )}
        </div>

        <div style={styles.modalFooter}>
          <button
            style={{ ...styles.button, ...styles.dangerButton }}
            onClick={onConfirm}
            disabled={quoteDeleteId === quoteDeleteTarget.quoteId}
          >
            {quoteDeleteId === quoteDeleteTarget.quoteId ? 'Deleting...' : 'Confirm Delete'}
          </button>
          <button
            style={{ ...styles.button, ...styles.secondaryButton }}
            onClick={onClose}
            disabled={quoteDeleteId === quoteDeleteTarget.quoteId}
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
};

type ExplanationModalProps = {
  open: boolean;
  styles: StylesMap;
  selectedCauseForExplanation: PossibleCause | null;
  explanationVerificationSteps: StepNode[];
  explanationLoading: boolean;
  explanationResult: any;
  StepList: React.ComponentType<{ nodes: StepNode[] }>;
  onClose: () => void;
};

export const TSFADExplanationModal: React.FC<ExplanationModalProps> = ({
  open,
  styles,
  selectedCauseForExplanation,
  explanationVerificationSteps,
  explanationLoading,
  explanationResult,
  StepList,
  onClose,
}) => {
  if (!open || !selectedCauseForExplanation) return null;

  return (
    <div style={styles.modal} onClick={onClose}>
      <div style={styles.modalContent} onClick={(e) => e.stopPropagation()}>
        <div style={styles.modalHeader}>
          <h3 style={styles.modalTitle}>Test Explanation</h3>
          <p style={{ color: '#6b7280', fontSize: '1rem', marginBottom: '1rem' }}>
            {selectedCauseForExplanation.description}
          </p>
        </div>

        {explanationVerificationSteps.length > 0 && (
          <div style={{ marginBottom: '1.25rem' }}>
            <div style={styles.testingLabel}>Test Steps</div>
            <StepList nodes={explanationVerificationSteps} />
          </div>
        )}

        {explanationLoading ? (
          <div style={{ textAlign: 'center', padding: '2rem' }}>
            <div>Loading explanation...</div>
          </div>
        ) : explanationResult ? (
          <div>
            <div style={{ marginBottom: '1.5rem' }}>
              <h4 style={{ marginBottom: '0.5rem' }}>Why this test is needed:</h4>
              <p style={{ fontSize: '0.875rem', lineHeight: '1.5' }}>
                {explanationResult.explanation}
              </p>
            </div>

            {explanationResult.failureMode && (
              <div style={{ marginBottom: '1rem' }}>
                <strong>Failure Mode Being Tested:</strong>
                <p style={{ fontSize: '0.875rem', marginTop: '0.25rem' }}>
                  {explanationResult.failureMode}
                </p>
              </div>
            )}

            {explanationResult.symptomsAddressed && explanationResult.symptomsAddressed.length > 0 && (
              <div style={{ marginBottom: '1rem' }}>
                <strong>Symptoms This Would Explain:</strong>
                <ul style={{ fontSize: '0.875rem', marginTop: '0.25rem', paddingLeft: '1.5rem' }}>
                  {explanationResult.symptomsAddressed.map((symptom: string, index: number) => (
                    <li key={index}>{symptom}</li>
                  ))}
                </ul>
              </div>
            )}

            {explanationResult.diagnosticValue && (
              <div style={{ marginBottom: '1rem' }}>
                <strong>What This Test Proves:</strong>
                <p style={{ fontSize: '0.875rem', marginTop: '0.25rem' }}>
                  {explanationResult.diagnosticValue}
                </p>
              </div>
            )}

            {explanationResult.cannotSkipBecause && (
              <div
                style={{
                  backgroundColor: '#fef3c7',
                  padding: '1rem',
                  borderRadius: '0.375rem',
                  fontSize: '0.875rem',
                }}
              >
                <strong>Cannot Skip Because:</strong>
                <p style={{ marginTop: '0.25rem' }}>
                  {explanationResult.cannotSkipBecause}
                </p>
              </div>
            )}
          </div>
        ) : null}

        <div style={{ ...styles.buttonGroup, marginTop: '1.5rem' }}>
          <button style={{ ...styles.button, ...styles.secondaryButton }} onClick={onClose}>
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

type FeedbackModalProps = {
  open: boolean;
  styles: StylesMap;
  feedbackForm: DocumentationFeedback;
  setFeedbackForm: React.Dispatch<React.SetStateAction<DocumentationFeedback>>;
  feedbackCause: PossibleCause | null;
  feedbackSuggestionDescription: string | null;
  onClose: () => void;
  onSubmit: () => void;
};

export const TSFADFeedbackModal: React.FC<FeedbackModalProps> = ({
  open,
  styles,
  feedbackForm,
  setFeedbackForm,
  feedbackCause,
  feedbackSuggestionDescription,
  onClose,
  onSubmit,
}) => {
  if (!open) return null;

  return (
    <div style={styles.modal} onClick={onClose}>
      <div style={styles.modalContent} onClick={(e) => e.stopPropagation()}>
        <div style={styles.modalHeader}>
          <h3 style={styles.modalTitle}>Flag Documentation Issue</h3>
          <button onClick={onClose} style={styles.closeButton}>
            X
          </button>
        </div>

        <div style={styles.modalBody}>
          <div style={{ marginBottom: '1rem' }}>
            <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
              Issue Type *
            </label>
            <select
              style={{ ...styles.select, width: '100%' }}
              value={feedbackForm.feedbackType}
              onChange={(e) => setFeedbackForm({ ...feedbackForm, feedbackType: e.target.value })}
            >
              <option value="MISSING_CAUSE">Missing Failure Cause</option>
              <option value="MISSING_TAGS">Missing Tags / Path Coverage</option>
              <option value="WRONG_PROCEDURE">Incorrect Procedure</option>
              <option value="WRONG_REFERENCE_IMAGE">Wrong Reference Image</option>
              <option value="UNCLEAR_INSTRUCTION">Unclear Instructions</option>
              <option value="MISSING_DIAGRAM">Missing/Poor Diagram</option>
              <option value="WRONG_PARTS">Incorrect Parts Info</option>
              <option value="SAFETY_CONCERN">Safety Issue</option>
            </select>
          </div>

          <div style={{ marginBottom: '1rem' }}>
            <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
              Severity *
            </label>
            <select
              style={{ ...styles.select, width: '100%' }}
              value={feedbackForm.severity}
              onChange={(e) => setFeedbackForm({ ...feedbackForm, severity: e.target.value })}
            >
              <option value="LOW">Low - Minor inconvenience</option>
              <option value="MEDIUM">Medium - Time wasted</option>
              <option value="HIGH">High - Significant impact</option>
              <option value="CRITICAL">Critical - Safety risk</option>
            </select>
          </div>

          <div style={{ marginBottom: '1rem' }}>
            <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
              Issue Title *
            </label>
            <input
              type="text"
              style={styles.input}
              placeholder="Brief description of the issue"
              value={feedbackForm.title}
              onChange={(e) => setFeedbackForm({ ...feedbackForm, title: e.target.value })}
            />
          </div>

          <div style={{ marginBottom: '1rem' }}>
            <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
              Detailed Description *
            </label>
            <textarea
              style={styles.textarea}
              placeholder="Explain the documentation issue in detail..."
              value={feedbackForm.description}
              onChange={(e) => setFeedbackForm({ ...feedbackForm, description: e.target.value })}
            />
          </div>

          <div style={{ marginBottom: '1rem' }}>
            <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
              Impact (Optional)
            </label>
            <input
              type="text"
              style={styles.input}
              placeholder="e.g., '30 minutes wasted', 'safety risk', 'wrong part ordered'"
              value={feedbackForm.impact}
              onChange={(e) => setFeedbackForm({ ...feedbackForm, impact: e.target.value })}
            />
          </div>

          {(feedbackCause || feedbackSuggestionDescription) && (
            <div
              style={{
                backgroundColor: '#f3f4f6',
                padding: '1rem',
                borderRadius: '0.375rem',
                fontSize: '0.875rem',
              }}
            >
              <strong>Related to:</strong>{' '}
              {feedbackCause?.description || feedbackSuggestionDescription}
            </div>
          )}
        </div>

        <div style={styles.modalFooter}>
          <button style={{ ...styles.button, ...styles.primaryButton }} onClick={onSubmit}>
            Submit Feedback
          </button>
          <button style={{ ...styles.button, ...styles.secondaryButton }} onClick={onClose}>
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
};

type KnowledgeCoverageModalProps = {
  open: boolean;
  styles: StylesMap;
  title: string;
  details: string;
  selectedPaths: string[];
  totalRecords?: number | null;
  filteredNoPath?: number | null;
  retrySubmitting?: boolean;
  actionError?: string | null;
  onSendFeedback: () => void;
  onEditProblem: () => void;
  onBackToCases: () => void;
};

export const TSFADKnowledgeCoverageModal: React.FC<KnowledgeCoverageModalProps> = ({
  open,
  styles,
  title,
  details,
  selectedPaths,
  totalRecords,
  filteredNoPath,
  retrySubmitting,
  actionError,
  onSendFeedback,
  onEditProblem,
  onBackToCases,
}) => {
  if (!open) return null;

  const hasSelectedPaths = Array.isArray(selectedPaths) && selectedPaths.length > 0;
  const showMetrics =
    typeof totalRecords === 'number' || typeof filteredNoPath === 'number';

  return (
    <div style={styles.modal} role="dialog" aria-modal="true" aria-label="TSFAD knowledge coverage issue">
      <div style={{ ...styles.modalContent, maxWidth: '640px' }} onClick={(e) => e.stopPropagation()}>
        <div style={styles.modalHeader}>
          <h3 style={{ ...styles.modalTitle, marginBottom: '0.75rem' }}>{title}</h3>
          <div
            style={{
              backgroundColor: '#fff7ed',
              border: '1px solid #fdba74',
              borderRadius: '0.75rem',
              padding: '0.95rem 1rem',
              color: '#9a3412',
              lineHeight: 1.5
            }}
          >
            {details}
          </div>
        </div>

        <div style={styles.modalBody}>
          {hasSelectedPaths && (
            <div>
              <div style={{ fontSize: '0.8rem', fontWeight: 700, letterSpacing: '0.08em', color: '#6b7280', textTransform: 'uppercase', marginBottom: '0.45rem' }}>
                System Paths Identified
              </div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
                {selectedPaths.map(path => (
                  <span
                    key={path}
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      padding: '0.4rem 0.7rem',
                      borderRadius: '999px',
                      backgroundColor: '#eff6ff',
                      color: '#1d4ed8',
                      fontWeight: 600,
                      fontSize: '0.92rem'
                    }}
                  >
                    {path}
                  </span>
                ))}
              </div>
            </div>
          )}

          {showMetrics && (
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
                gap: '0.75rem'
              }}
            >
              <div
                style={{
                  border: '1px solid #dbeafe',
                  borderRadius: '0.75rem',
                  padding: '0.85rem 0.95rem',
                  backgroundColor: '#f8fbff'
                }}
              >
                <div style={{ fontSize: '0.78rem', color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.08em' }}>
                  Total Knowledge Nodes Reviewed
                </div>
                <div style={{ marginTop: '0.35rem', fontSize: '1.3rem', fontWeight: 700, color: '#111827' }}>
                  {typeof totalRecords === 'number' ? totalRecords : '-'}
                </div>
              </div>
              <div
                style={{
                  border: '1px solid #fee2e2',
                  borderRadius: '0.75rem',
                  padding: '0.85rem 0.95rem',
                  backgroundColor: '#fffafa'
                }}
              >
                <div style={{ fontSize: '0.78rem', color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.08em' }}>
                  Missing Path Coverage
                </div>
                <div style={{ marginTop: '0.35rem', fontSize: '1.3rem', fontWeight: 700, color: '#991b1b' }}>
                  {typeof filteredNoPath === 'number' ? filteredNoPath : '-'}
                </div>
              </div>
            </div>
          )}
        </div>

        <div style={styles.modalFooter}>
          <button
            style={{
              ...styles.button,
              ...styles.primaryButton,
              fontWeight: 700,
              minHeight: '3rem',
              boxShadow: '0 10px 24px rgba(59,130,246,0.2)'
            }}
            onClick={onSendFeedback}
            disabled={Boolean(retrySubmitting)}
          >
            Send Feedback to Knowledge Team
          </button>
          <button
            style={{ ...styles.button, ...styles.secondaryButton, minHeight: '3rem' }}
            onClick={onEditProblem}
            disabled={Boolean(retrySubmitting)}
          >
            {retrySubmitting ? 'Logging Feedback...' : 'Edit Problem and Retry'}
          </button>
          <button
            style={{ ...styles.button, ...styles.secondaryButton, minHeight: '3rem' }}
            onClick={onBackToCases}
            disabled={Boolean(retrySubmitting)}
          >
            Back to Cases
          </button>
        </div>
        {actionError && (
          <div style={{ marginTop: '0.85rem', color: '#b91c1c', fontSize: '0.92rem' }}>
            {actionError}
          </div>
        )}
      </div>
    </div>
  );
};
